// Graph with ECharts - Fully Customizable 3D-capable Plot
// Flexible axis selection for comprehensive cell analysis

import { useMemo } from 'preact/hooks'
import { isInCompare, toggleCompare } from '../../state/filterStore.js'
import {
    freePlotBenchmarkX,
    freePlotBenchmarkY,
    freePlotColorBy,
    freePlotConnectPoints,
    highlightedCellIds,
    freePlotMarkerShape,
    freePlotShowBenchmarks,
    freePlotShowGrid,
    freePlotShowLabels,
    freePlotShowLegend,
    freePlotSizeBy,
    freePlotXAxis,
    freePlotXAxisScale,
    freePlotYAxis,
    freePlotYAxisScale,
    freePlotZAxis
} from '../../state/globalSignals.js'
import { activePreset, currentClusterData, currentRegression, getPreset, isRegressionReliable, plotMode } from '../../state/plotPresets.js'
import { isCellLockedAtLimit, isCellUnlockedToday, isDemoCatalogUnlocked, unlockedTodayIds } from '../../state/demoLimitStore.js'
import { blockDemoExport, unlockCell } from '../../services/demoLimitService.js'
import { getWatermarkGraphic } from '../../utils/chartWatermark.js'
import { formatValue } from '../../utils/formatUtils.js'
import { generateCellSlug } from '../../utils/urlUtils.js'
import { EXPLORER_KEY_TO_CELL_SHAPE_KEY } from '../../modules/explorerCellShape.js'
import EChartsComponent from '../visualizations/EChartsComponent.jsx'

// Task B4 (demo scraping-friction follow-up, defense-in-depth): the NAMED
// tooltip formatters below interpolate DB-sourced product fields (name,
// manufacturer, chemistry, format) directly into raw HTML strings. These are
// Batemo's own catalogue data (low risk), but escape them anyway so a
// special-character/markup value in any of those fields can never break out
// of the tooltip HTML. Module-level so it isn't re-created per render (same
// spirit as the anon branch's SAFE_TOKEN_RE, Task 11). The anon branches
// already use whitelist-validated id/handle and are untouched.
export function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]))
}

// Expose global handler for tooltip buttons (reuse same function)
if (typeof window !== 'undefined' && !window.__toggleCompare) {
  window.__toggleCompare = (cellId) => {
    toggleCompare(cellId)
    setTimeout(() => {
      const event = new CustomEvent('compare-toggle', { detail: { cellId } })
      window.dispatchEvent(event)
    }, 0)
  }
}

// Anonymous-point tooltip "Unlock" button (demo scraping-friction, Task 11):
// spends the same Deep-Dive token as opening the cell — NOT the PRO-upsell
// path. unlockCell handles a 429 (at today's limit) itself by opening the
// conversion modal; any other error is swallowed here so a transient failure
// never crashes the tooltip. Fire-and-forget from raw tooltip HTML, mirroring
// the window.__toggleCompare pattern above.
if (typeof window !== 'undefined' && !window.__unlockPlotCell) {
  window.__unlockPlotCell = (productId, handle) => {
    unlockCell({ handle: handle || undefined, productId, surface: 'plot' })
      .then((result) => {
        // Success (result is truthy — a 429 hit resolves to null and is left
        // alone so the user still sees the conversion modal/limit copy).
        // DON'T close the tooltip (that used to leave the user hunting for
        // which of 300+ points they'd just unlocked). Instead remember the
        // cell: the reactive post-unlock explorer reload (demoUnlockVersion)
        // repopulates allCells with the exact/named values and re-renders the
        // plot; the chart's 'finished' handler then re-opens the tooltip on
        // this same point — now showing the unlocked identity — and the point
        // keeps a persistent highlight so it stays findable.
        if (result) {
          window.__justUnlockedCellId = productId
          // Safety net: if the re-render never matches this id (e.g. the user
          // switches to an axis where this cell has no value before the reload
          // lands), don't let the flag linger and pop a tooltip on some later
          // render. The reopen clears it on match; this clears it otherwise.
          clearTimeout(window.__justUnlockedTimer)
          window.__justUnlockedTimer = setTimeout(() => {
            window.__justUnlockedCellId = null
          }, 8000)
        }
      })
      .catch((err) => {
        console.warn('[FreePlot] unlockCell failed:', err?.message || err)
      })
  }
}

// Global state for pinned tooltip
if (typeof window !== 'undefined') {
  window.__pinnedTooltipCellId = null
  window.__freePlotChartInstance = null

  window.__pinTooltip = (cellId) => {
    window.__pinnedTooltipCellId = cellId
  }

  window.__closeTooltip = () => {
    window.__pinnedTooltipCellId = null
    if (window.__freePlotChartInstance) {
      window.__freePlotChartInstance.dispatchAction({
        type: 'hideTip'
      })
    }
    // Trigger custom event to notify chart component
    const event = new CustomEvent('tooltip-close')
    window.dispatchEvent(event)
  }
}

// ECharts GL for 3D support
// Import the GL library which auto-registers components with echarts
import 'echarts-gl'

// Note: echarts-gl auto-registers itself when imported

/**
 * Available KPIs for all axes
 * Organized by category for better UX
 *
 * Naming convention: "Metric / condition / variant (unit)"
 *   condition = C/10, peak, continuous, standard
 *   variant   = absolute, gravimetric, volumetric
 */
export const AVAILABLE_KPIS = {
  // ── Electrical ──────────────────────────────────────────────────────
  electrical: [
    // Energy (C/10)
    { id: 'energy_c10_abs',  label: 'Energy / C/10 / absolute',     unit: 'Wh',    field: 'electrical.E_c10_Wh' },
    { id: 'energy_c10_grav', label: 'Energy / C/10 / gravimetric',  unit: 'Wh/kg', field: 'electrical.E_grav_Wh_per_kg' },
    { id: 'energy_c10_vol',  label: 'Energy / C/10 / volumetric',   unit: 'Wh/L',  field: 'electrical.E_vol_Wh_per_L' },

    // Capacity (C/10)
    { id: 'capacity_c10_abs',  label: 'Capacity / C/10 / absolute',    unit: 'Ah',    field: 'electrical.C_c10_Ah' },
    { id: 'capacity_c10_grav', label: 'Capacity / C/10 / gravimetric', unit: 'Ah/kg', field: 'electrical.C_grav_Ah_per_kg' },
    { id: 'capacity_c10_vol',  label: 'Capacity / C/10 / volumetric',  unit: 'Ah/L',  field: 'electrical.C_vol_Ah_per_L' },

    // Power — peak
    { id: 'power_peak_abs',  label: 'Power / peak / absolute',     unit: 'W',    field: 'electrical.P_peak_W' },
    { id: 'power_peak_grav', label: 'Power / peak / gravimetric',  unit: 'W/kg', field: 'electrical.P_grav_W_per_kg' },
    { id: 'power_peak_vol',  label: 'Power / peak / volumetric',   unit: 'W/L',  field: 'electrical.P_vol_W_per_L' },

    // Power — continuous
    { id: 'power_cont_abs',  label: 'Power / continuous / absolute',    unit: 'W',    field: 'electrical.P_cont_W' },
    { id: 'power_cont_grav', label: 'Power / continuous / gravimetric', unit: 'W/kg', field: 'electrical.P_cont_W', conversion: (v, cell) => { const m = cell?.mechanical?.m_cell; return (v != null && m != null) ? v / m : null; } },
    { id: 'power_cont_vol',  label: 'Power / continuous / volumetric',  unit: 'W/L',  field: 'electrical.P_cont_W', conversion: (v, cell) => { const vol = cell?.mechanical?.vol_cell_cm3; return (v != null && vol != null) ? (v / vol) * 1000 : null; } },

    // Current — peak
    { id: 'current_peak_abs',  label: 'Current / peak / absolute',     unit: 'A',    field: 'electrical.I_peak_A' },
    { id: 'current_peak_grav', label: 'Current / peak / gravimetric',  unit: 'A/kg', field: 'electrical.I_grav_A_per_kg' },
    { id: 'current_peak_vol',  label: 'Current / peak / volumetric',   unit: 'A/L',  field: 'electrical.I_vol_A_per_L' },

    // Current — continuous
    { id: 'current_cont_abs',  label: 'Current / continuous / absolute',    unit: 'A',    field: 'electrical.I_cont_A' },
    { id: 'current_cont_grav', label: 'Current / continuous / gravimetric', unit: 'A/kg', field: 'electrical.I_cont_A', conversion: (v, cell) => { const m = cell?.mechanical?.m_cell; return (v != null && m != null) ? v / m : null; } },
    { id: 'current_cont_vol',  label: 'Current / continuous / volumetric',  unit: 'A/L',  field: 'electrical.I_cont_A', conversion: (v, cell) => { const vol = cell?.mechanical?.vol_cell_cm3; return (v != null && vol != null) ? (v / vol) * 1000 : null; } },

    // Voltage
    { id: 'voltage', label: 'Voltage', unit: 'V', field: 'electrical.V_mean' },

    // Impedance
    { id: 'R_ac_25',  label: 'Impedance / AC',            unit: 'mΩ', field: 'electrical.R_ac_mOhm' },
    { id: 'R_pol_25', label: 'Impedance / Polarisation',   unit: 'mΩ', field: 'electrical.R_pol_25' },
    { id: 'R_dc_25',  label: 'Impedance / DCIR',           unit: 'mΩ', field: 'electrical.R_dc_mOhm' },
  ],

  // ── Thermal ─────────────────────────────────────────────────────────
  thermal: [
    // Heat Power (Power Loss at standard condition)
    { id: 'P_loss_dc_25_abs',  label: 'Heat Power / standard / absolute',     unit: 'W',    field: 'thermal.P_loss_dc_25' },
    { id: 'P_loss_dc_25_grav', label: 'Heat Power / standard / gravimetric',  unit: 'W/kg', field: 'thermal.P_loss_dc_25', conversion: (v, cell) => { const m = cell?.mechanical?.m_cell; return (v != null && m != null) ? v / m : null; } },
    { id: 'P_loss_dc_25_vol',  label: 'Heat Power / standard / volumetric',   unit: 'W/L',  field: 'thermal.P_loss_dc_25', conversion: (v, cell) => { const vol = cell?.mechanical?.vol_cell_cm3; return (v != null && vol != null) ? (v / vol) * 1000 : null; } },

    // Heat Energy (Energy Loss at standard condition)
    { id: 'W_loss_dc_25_abs',  label: 'Heat Energy / standard / absolute',    unit: 'Wh',    field: 'thermal.W_loss_dc_25' },
    { id: 'W_loss_dc_25_grav', label: 'Heat Energy / standard / gravimetric', unit: 'Wh/kg', field: 'thermal.W_loss_dc_25', conversion: (v, cell) => { const m = cell?.mechanical?.m_cell; return (v != null && m != null) ? v / m : null; } },
    { id: 'W_loss_dc_25_vol',  label: 'Heat Energy / standard / volumetric',  unit: 'Wh/L',  field: 'thermal.W_loss_dc_25', conversion: (v, cell) => { const vol = cell?.mechanical?.vol_cell_cm3; return (v != null && vol != null) ? (v / vol) * 1000 : null; } },

    // Max Temperature & Efficiency
    { id: 'T_max_dc_25',  label: 'Max Temperature',  unit: '°C', field: 'thermal.T_max_dc_25' },
    { id: 'eta_dc_25',    label: 'Efficiency',        unit: '%',  field: 'electrical.eta_dc_pct' },
  ],

  // ── Mechanical ──────────────────────────────────────────────────────
  mechanical: [
    { id: 'mass',         label: 'Mass',         unit: 'g',   field: 'mechanical.mass_g' },
    { id: 'volume',       label: 'Volume',       unit: 'cm³', field: 'mechanical.vol_cell_cm3' },
    { id: 'surface_area', label: 'Surface Area', unit: 'cm²', field: 'mechanical.area_cm2' },
  ],

  // ── Quality ─────────────────────────────────────────────────────────
  quality: [
    // Post-Formation Capacity Loss
    { id: 'aging_cap_loss_abs', label: 'Post-Formation Capacity Loss / absolute', unit: 'Ah', field: 'aging.CAP_loss', conversion: (v) => (v != null ? Math.abs(v) : null) },
    { id: 'aging_cap_loss',     label: 'Post-Formation Capacity Loss / relative', unit: '%',  field: 'aging.CAP_loss_rel', conversion: (v) => Math.abs(v * 100) },

    // Post-Formation Impedance Growth
    { id: 'aging_eis_increase_abs', label: 'Post-Formation Impedance Growth / absolute', unit: 'mΩ', field: 'aging.EIS_increase' },
    { id: 'aging_eis_increase',     label: 'Post-Formation Impedance Growth / relative', unit: '%',  field: 'aging.EIS_increase_rel', conversion: (v) => v * 100 },

    // Cell-to-Cell Capacity Deviation
    { id: 'batch_cap_variation_abs', label: 'Cell-to-Cell Capacity Deviation / absolute', unit: 'Ah', field: 'batch.CAP_spread_std_abs' },
    { id: 'batch_cap_variation',     label: 'Cell-to-Cell Capacity Deviation / relative', unit: '%',  field: 'batch.CAP_spread_std_rel', conversion: (v) => v * 100 },

    // Cell-to-Cell Impedance Deviation
    { id: 'batch_eis_variation_abs', label: 'Cell-to-Cell Impedance Deviation / absolute', unit: 'mΩ', field: 'batch.EIS_spread_std_abs' },
    { id: 'batch_eis_variation',     label: 'Cell-to-Cell Impedance Deviation / relative', unit: '%',  field: 'batch.EIS_spread_std_rel', conversion: (v) => v * 100 },
  ],

  // ── Date ────────────────────────────────────────────────────────────
  date: [
    {
      id: 'year',
      label: 'Year',
      unit: '',
      field: 'date',
      conversion: (dateString, cell) => {
        if (dateString && typeof dateString === 'string') {
          try {
            const date = new Date(dateString)
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear()
              const startOfYear = new Date(year, 0, 1)
              const dayOfYear = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24))
              const daysInYear = (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 366 : 365
              const decimalYear = year + (dayOfYear / daysInYear)
              if (isFinite(decimalYear) && decimalYear > 1900 && decimalYear < 2100) {
                return decimalYear
              }
            }
          } catch (e) {
            // Date parsing failed, use fallback
          }
        }
        const yearValue = cell?.year
        if (yearValue != null && typeof yearValue === 'number' && isFinite(yearValue)) {
          return yearValue
        }
        return null
      }
    },
  ]
}

// Flatten all KPIs for easy lookup
export const ALL_KPIS = Object.values(AVAILABLE_KPIS).flat()

/**
 * Get KPI value from cell data
 */
function getKPIValue(cell, kpiId) {
  const kpi = ALL_KPIS.find(k => k.id === kpiId)
  if (!kpi) return null

  // Support nested field paths (e.g., 'mechanical.m_cell')
  let rawValue
  if (kpi.field.includes('.')) {
    const parts = kpi.field.split('.')
    rawValue = cell
    for (const part of parts) {
      if (rawValue == null) return null
      rawValue = rawValue[part]
    }
  } else {
    rawValue = cell[kpi.field]
  }

  if (rawValue == null) return null

  return kpi.conversion ? kpi.conversion(rawValue, cell) : rawValue
}

/**
 * Demo scraping-friction (Task 11): conditional anonymous mode.
 * =================================================================
 * When a GATED dimension (an axis whose KPI maps to a premium field the
 * backend withholds for demo rows, or color/size pointed at chemistry) is
 * active, a not-yet-unlocked-today demo cell's point must plot the coarse
 * `cell.binned[kpi.field]` band center instead of the exact value, and its
 * payload must never carry identity (name/chemistry) — see
 * `getPlotAxisValue`/`buildAnonPointData` below. Free-only dimensions and
 * already-unlocked cells keep the existing exact/named path unchanged.
 *
 * The gated shape-key set is derived from the SAME backend-key → cell-shape
 * mapping that builds `cell._gated` (explorerCellShape.js), so this stays
 * automatically in sync with whatever the backend tiers next.
 */
const GATED_SHAPE_KEYS = new Set(Object.values(EXPLORER_KEY_TO_CELL_SHAPE_KEY))

/** True when a FreePlot KPI id maps to a premium field gated for demo rows. */
export function isGatedKpi(kpiId) {
  const field = ALL_KPIS.find(k => k.id === kpiId)?.field
  return field != null && GATED_SHAPE_KEYS.has(field)
}

/** Color/size dimension ids that group by chemistry (also gated for demo). */
const CHEMISTRY_DIMENSIONS = new Set(['chemistry', 'main-chemistry', 'cathode-chemistry', 'anode-chemistry'])

/**
 * True when a `colorBy`/`sizeBy` dimension id counts as gated: either a
 * chemistry grouping (categorical — the backend never withholds the value,
 * but it's tiered by the same governing rule as Grid/Table, see
 * `isChemistryLockedForDemo`), or a KPI id pointed at a gated field (e.g.
 * sizing markers by impedance).
 */
export function isGatedColorOrSize(dimId) {
  if (dimId == null || dimId === 'none') return false
  if (CHEMISTRY_DIMENSIONS.has(dimId)) return true
  return isGatedKpi(dimId)
}

/**
 * The single bucket every locked point falls into when the colour dimension is
 * itself gated. Without it, colouring by chemistry paints each locked cell in
 * its real chemistry's colour — the value is present client-side (the Table
 * merely renders "—" for it), so the legend hands out exactly the field the
 * tooltip refuses to name. Reported by BA 2026-08-05: "dann switch ich color
 * by, wo mir die Chemie anzeigt, was eigentlich nicht frei ist".
 */
export const LOCKED_CATEGORY = 'Locked'
const LOCKED_CATEGORY_COLOR = '#6b6f76'

/**
 * Resolve one axis' plotted value for a cell. When the point must render
 * anonymized (a gated dim is active AND the cell isn't unlocked today) AND
 * this specific axis is itself a gated KPI, read the coarse binned band
 * center instead of the exact value; a FREE axis on the same anonymized
 * point stays exact (only identity + the gated numbers are protected).
 * `null` (missing binned value, e.g. nothing gated for this row) propagates
 * up so the existing "skip cells with missing data" guard drops the point.
 */
export function getPlotAxisValue(cell, kpiId, anonymizePoint) {
  if (anonymizePoint && isGatedKpi(kpiId)) {
    const kpi = ALL_KPIS.find(k => k.id === kpiId)
    const field = kpi?.field
    const binned = field ? cell?.binned?.[field] : null
    if (binned == null) return null
    // Fix C1 (Task 11 review): the binned band-center is the RAW absolute
    // value for the underlying field (e.g. thermal.P_loss_dc_25 in W), same
    // as `getKPIValue`'s `rawValue` before conversion. Gravimetric/volumetric
    // KPIs on that same field (e.g. P_loss_dc_25_grav/vol) apply
    // `kpi.conversion(value, cell)` to turn the absolute value into a
    // per-mass/per-volume one — that conversion must run here too, or the
    // binned point plots in the WRONG UNIT (absolute W instead of W/kg).
    // Leak-safety is unaffected: the band center stays quantized; mass/volume
    // are free (ungated) fields, so dividing by them adds no precision back.
    //
    // NOTE: today the backend only bins premium/gated ABSOLUTE fields (impedance,
    // thermal, etc.) — never the quality-tier pct/relative KPIs (batch/aging),
    // which use `conversion: v => v * 100` on an already-relative raw value. If
    // the backend ever starts binning one of those quality keys, `remapBinned`
    // (explorerCellShape.js) must mirror that `* 100` scaling on the binned
    // value BEFORE it lands in `cell.binned`, or this generic
    // `kpi.conversion(binned, cell)` call would double-apply/misapply the
    // scaling here.
    return kpi.conversion ? kpi.conversion(binned, cell) : binned
  }
  return getKPIValue(cell, kpiId)
}

/**
 * Build the anonymous (identity-stripped) point payload for a gated,
 * not-yet-unlocked demo cell. Deliberately carries ONLY what the Unlock
 * button needs (id/handle) plus the `__anon` marker — never `cell.name`,
 * chemistry, or the full cell object, so identity never reaches the
 * DOM/tooltip/chart state for a locked point.
 */
export function buildAnonPointData(cell) {
  return { id: cell.id, handle: cell.handle ?? null, __anon: true }
}

/**
 * Get category name for coloring
 */
/**
 * Helper: Get category name from cell based on colorBy dimension
 * Uses consistent data structure across all plots
 * IMPORTANT: Always return non-empty string for consistent hashing
 */
function getCategoryName(cell, colorBy) {
  let value

  if (colorBy === 'main-chemistry') {
    value = cell.chemical?.main_chemistry
  } else if (colorBy === 'cathode-chemistry') {
    // Use cathode_material_group with fallback
    value = cell.chemical?.cathode_material_group ||
            cell.chemical?.main_chemistry ||
            cell.chemical?.cathode
  } else if (colorBy === 'anode-chemistry') {
    // NEW: Use anode_material_group with fallback
    value = cell.chemical?.anode_material_group || cell.chemical?.anode
  } else if (colorBy === 'chemistry') {
    // Legacy: combined chemistry (for backwards compatibility)
    value = cell.chemical?.cathode_material_group || cell.chemical?.main_chemistry || cell.chemical?.cathode
  } else if (colorBy === 'technology') {
    value = cell.general?.technology || cell.technology
  } else if (colorBy === 'format') {
    value = cell.mechanical?.format_group || cell.mechanical?.format
  } else if (colorBy === 'housing') {
    value = cell.mechanical?.shape
  } else if (colorBy === 'manufacturer') {
    value = cell.general?.manufacturer || cell.manufacturer
  } else if (colorBy === 'year') {
    // Support both date string (ISO) and year integer
    const dateStr = cell.general?.date || cell.date
    if (dateStr) {
      const year = parseInt(dateStr.split('-')[0])
      if (!isNaN(year)) return year.toString()
    }
    // Fallback to year field directly
    const yearValue = cell.general?.year || cell.year
    if (yearValue != null && !isNaN(yearValue)) return yearValue.toString()
    return 'Unknown'
  }

  // Normalize empty/null/undefined to consistent 'Unknown'
  if (!value || value.trim() === '') {
    return 'Unknown'
  }

  return value.trim()
}

// Import central color system
import { getCategoryColor as getCentralCategoryColor } from '../../utils/plotColors.js'

/**
 * Get category color (uses central color system)
 */
function getCategoryColor(category, colorBy) {
  return getCentralCategoryColor(colorBy, category)
}

/**
 * Convert hex color to rgba with opacity
 */
function hexToRgba(hex, opacity) {
  if (!hex) return `rgba(128, 128, 128, ${opacity})`
  // Remove # if present
  hex = hex.replace('#', '')
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Get size value for marker scaling
 */
function getCellSizeValue(cell, sizeBy) {
  // Handle legacy size options (from context bar)
  if (sizeBy === 'batch-variation') return cell.batch?.CAP_spread_std_rel ? Math.abs(cell.batch.CAP_spread_std_rel * 100) : 0
  if (sizeBy === 'aging') return cell.aging?.CAP_loss_rel ? Math.abs(cell.aging.CAP_loss_rel * 100) : 0
  if (sizeBy === 'capacity') return cell.electrical?.C_c10_Ah || 0
  if (sizeBy === 'mass') return cell.mechanical?.m_cell_g || 0
  if (sizeBy === 'year') {
    if (!cell.date) return 0
    const year = parseInt(cell.date.split('-')[0])
    return isNaN(year) ? 0 : year
  }

  // Handle new KPI-based size options
  const value = getKPIValue(cell, sizeBy)
  return value != null ? value : 0
}

/**
 * Normalize size to pixel range
 */
function normalizeSizeToPixels(value, min, max, minPx = 6, maxPx = 20) {
  if (max === min) return (minPx + maxPx) / 2
  return minPx + ((value - min) / (max - min)) * (maxPx - minPx)
}

/**
 * Calculate Pareto Frontier for Graph
 * Points that are not dominated by any other point (maximize both X and Y)
 */
function calculateParetoFrontier(points) {
  if (!points || points.length === 0) return []

  // Filter valid points
  const validPoints = points.filter(p => {
    const x = p.value?.[0]
    const y = p.value?.[1]
    return x != null && y != null && !isNaN(x) && !isNaN(y) && x > 0 && y > 0
  })

  if (validPoints.length === 0) return []

  // Sort by X ascending
  const sorted = [...validPoints].sort((a, b) => a.value[0] - b.value[0])

  const pareto = []
  let maxY = -Infinity

  for (const point of sorted) {
    const y = point.value[1]
    // Point is on Pareto if Y is higher than all previous
    if (y > maxY) {
      pareto.push(point)
      maxY = y
    }
  }

  console.log('[FreePlot Pareto] Found', pareto.length, 'frontier points from', validPoints.length, 'valid points')

  return pareto
}

/**
 * Calculate linear regression for trendline
 * Returns { slope, intercept, minX, maxX, r2 }
 */
function calculateLinearRegression(points) {
  if (!points || points.length < 2) return null

  // Filter valid points with x and y values
  const validPoints = points.filter(p => {
    const x = p.value?.[0]
    const y = p.value?.[1]
    return x != null && y != null && !isNaN(x) && !isNaN(y)
  })

  if (validPoints.length < 2) return null

  const n = validPoints.length
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0
  let minX = Infinity, maxX = -Infinity

  for (const point of validPoints) {
    const x = point.value[0]
    const y = point.value[1]
    sumX += x
    sumY += y
    sumXY += x * y
    sumX2 += x * x
    sumY2 += y * y
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
  }

  const denominator = n * sumX2 - sumX * sumX
  if (denominator === 0) return null

  const slope = (n * sumXY - sumX * sumY) / denominator
  const intercept = (sumY - slope * sumX) / n

  // Calculate R² for quality indication
  const meanY = sumY / n
  let ssTotal = 0, ssResidual = 0
  for (const point of validPoints) {
    const x = point.value[0]
    const y = point.value[1]
    const predicted = slope * x + intercept
    ssTotal += (y - meanY) ** 2
    ssResidual += (y - predicted) ** 2
  }
  const r2 = ssTotal > 0 ? 1 - (ssResidual / ssTotal) : 0

  console.log('[FreePlot Trend] Regression:', { slope: slope.toFixed(4), intercept: intercept.toFixed(2), r2: r2.toFixed(3), points: n })

  return { slope, intercept, minX, maxX, r2, n: validPoints.length }
}

export default function FreePlotECharts({ cells = [] }) {
  // Read all signal values
  const xAxisKPI = freePlotXAxis.value
  const yAxisKPI = freePlotYAxis.value
  const zAxisKPI = freePlotZAxis.value
  const colorBy = freePlotColorBy.value
  const sizeBy = freePlotSizeBy.value

  const showBenchmarks = freePlotShowBenchmarks.value
  const showLegend = freePlotShowLegend.value
  const xAxisScale = freePlotXAxisScale.value
  const yAxisScale = freePlotYAxisScale.value
  const markerShape = freePlotMarkerShape.value
  const showGrid = freePlotShowGrid.value
  const connectPoints = freePlotConnectPoints.value

  // Determine if 3D mode is needed
  const is3DMode = zAxisKPI !== 'none'

  // Demo users explore freely but the export take-away is PRO-only: swap the
  // native ECharts saveAsImage (which downloads directly) for a look-alike
  // button that opens the upsell instead of downloading (see toolbox below).
  const isDemo = isDemoCatalogUnlocked()

  console.log('[FreePlot] Rendering with:', {
    cellCount: cells.length,
    xAxisKPI,
    yAxisKPI,
    zAxisKPI,
    colorBy,
    sizeBy,
    is3DMode
  })

  // Prepare chart data with global size normalization
  // Separate accessible cells from locked (shadow) cells
  const { chartDataByCategory, categories, allChartData, sizeRange, shadowChartData } = useMemo(() => {
    const grouped = {}
    const allData = []
    const shadowData = []
    const sizeValues = []

    // Split cells into accessible and locked. Demo users have the full catalogue
    // unlocked (same as Grid/Table via isDemoCatalogUnlocked), so treat every cell
    // as accessible — no grey "Locked" shadow series for them.
    let accessibleCells = isDemo ? [...cells] : cells.filter(c => !c.is_locked)
    const lockedCells = isDemo ? [] : cells.filter(c => c.is_locked)

    // Exclude cells with unknown/missing chemistry when preset requests it
    const preset = activePreset.value ? getPreset(activePreset.value) : null
    if (preset?.excludeUnknownChemistry) {
      accessibleCells = accessibleCells.filter(cell => {
        const chem = cell.chemical?.main_chemistry
        return chem && chem.trim() !== '' && chem.toLowerCase() !== 'analysis ongoing'
      })
    }

    // First pass: collect size values for global normalization (accessible only)
    if (sizeBy !== 'none') {
      accessibleCells.forEach(cell => {
        const sizeValue = getCellSizeValue(cell, sizeBy)
        if (sizeValue != null && sizeValue > 0) sizeValues.push(sizeValue)
      })
    }

    const minSize = sizeValues.length > 0 ? Math.min(...sizeValues) : 0
    const maxSize = sizeValues.length > 0 ? Math.max(...sizeValues) : 1

    // Is the COLOUR / SIZE dimension itself a gated one? Computed once per
    // render. A locked point must not encode a gated value in its colour (the
    // legend would name it) or in its marker radius (a continuous read-out of
    // the very number that is binned on the axes).
    const colorDimGated = isGatedColorOrSize(colorBy)
    const sizeDimGated = isGatedColorOrSize(sizeBy)

    // Second pass: prepare data points for accessible cells
    let skippedCount = 0
    accessibleCells.forEach(cell => {
      // Anonymize this point when this demo cell hasn't been unlocked today.
      // Never true for non-demo tiers (isDemo false) or once the cell is
      // unlocked — that falls through to the existing exact/named path.
      //
      // STRICT MODE, enabled 2026-08-05 (BA). Until now this ALSO required a
      // gated dimension to be active (`isAnyGatedDimActive`), which left the
      // hole the original author wrote down and left open pending approval
      // (Task 12, spec §5, Björn's ③): a plot with only FREE axes and colour
      // showed the cell's NAME in the tooltip. Switching colour-by to chemistry
      // then anonymised the same point — at the same screen position. So you
      // read the name in the free view, switched, and knew which "Locked Cell"
      // you were looking at. Identity is now withheld regardless of which
      // dimensions are on screen, which is what "kill the free views" means:
      // there is no longer a view in which a locked cell names itself.
      //
      // Note this hides IDENTITY only. A free axis still plots its exact value
      // (`getPlotAxisValue` bins gated KPIs alone) — free data stays free, it
      // just no longer comes with a name attached.
      //
      // Final-review fix (Task 11): also require the ROW ITSELF to actually
      // carry server-side gating. Internal (@batemo.de) accounts on a DEMO
      // subscription are exempt server-side (is_demo_user → False) — the
      // backend sends them exact values with `binned: {}` (see
      // explorerCellShape.js: "Both null/empty for unlocked / exempt / PRO
      // rows"). Gating purely off the client tier caused those mixed accounts
      // to anonymize every point on a gated dimension, and since exempt rows
      // have no binned value, `getPlotAxisValue` returned null → the point was
      // dropped entirely (empty plot). A genuine (non-exempt) demo row always
      // carries at least one binned field, so this doesn't affect real demo
      // users.
      const rowActuallyGated = cell.binned != null && Object.keys(cell.binned).length > 0
      const anonymizePoint = isDemo && rowActuallyGated && !isCellUnlockedToday(cell.id)

      const xValue = getPlotAxisValue(cell, xAxisKPI, anonymizePoint)
      const yValue = getPlotAxisValue(cell, yAxisKPI, anonymizePoint)
      const zValue = zAxisKPI !== 'none' ? getPlotAxisValue(cell, zAxisKPI, anonymizePoint) : null

      // Skip cells with missing data
      if (xValue == null || yValue == null) {
        skippedCount++
        return
      }

      // Z-Axis logic: if Z-axis selected, use for color or size
      let category, color, symbolSize = 10

      if (zAxisKPI !== 'none' && zValue != null && colorBy === 'z-axis') {
        // Use Z-axis for color (gradient from blue to red)
        const allZValues = cells.map(c => getKPIValue(c, zAxisKPI)).filter(v => v != null && v > 0)
        const minZ = Math.min(...allZValues)
        const maxZ = Math.max(...allZValues)
        const normalized = (zValue - minZ) / (maxZ - minZ)
        // Gradient: blue (low) -> cyan -> green -> yellow -> orange -> red (high)
        const hue = 240 - (normalized * 240) // 240 (blue) to 0 (red)
        color = `hsl(${hue}, 70%, 60%)`
        category = 'Z-Axis Gradient'
      } else if (anonymizePoint && colorDimGated) {
        // One grey bucket for every locked point — the legend must not become a
        // read-out of the gated field.
        category = LOCKED_CATEGORY
        color = LOCKED_CATEGORY_COLOR
      } else {
        // Standard color by category
        category = getCategoryName(cell, colorBy)
        color = getCategoryColor(category, colorBy)
      }

      // Calculate marker size
      if (zAxisKPI !== 'none' && zValue != null && sizeBy === 'z-axis') {
        // Use Z-axis for size
        const allZValues = cells.map(c => getKPIValue(c, zAxisKPI)).filter(v => v != null && v > 0)
        const minZ = Math.min(...allZValues)
        const maxZ = Math.max(...allZValues)
        symbolSize = normalizeSizeToPixels(zValue, minZ, maxZ)
      } else if (sizeBy !== 'none' && !(anonymizePoint && sizeDimGated)) {
        const sizeValue = getCellSizeValue(cell, sizeBy)
        if (sizeValue > 0) {
          symbolSize = normalizeSizeToPixels(sizeValue, minSize, maxSize)
        }
      }
      // …and a locked point sized by a gated dimension keeps the default
      // radius: marker size is a continuous scale, so sizing by the exact
      // value would hand back the precision the binned axes withhold.

      // Strong, persistent highlight: the point is enlarged and gets a bright
      // white ring + orange glow so it stands out among hundreds of others.
      //
      // TWO sources, one appearance. It was written for cells a demo user has
      // unlocked today (so they stay findable after the post-unlock re-render),
      // and Ion now drives the same emphasis via `highlightedCellIds` to answer
      // "show me this cell compared to the others" — the styling was already
      // here and proven, only the way in was missing. Deliberately the same look:
      // "this point matters" is one idea, and a second visual language for it
      // would make the chart harder to read, not more informative.
      const highlighted = highlightedCellIds.value?.has(cell.id) === true
      const unlockedHighlight = highlighted || (isDemo && isCellUnlockedToday(cell.id))
      if (unlockedHighlight) symbolSize = Math.max(Math.round(symbolSize * 1.7), 16)
      const gradientItemStyle = (zAxisKPI !== 'none' && sizeBy === 'z-axis')
        ? { color, borderColor: 'rgba(255, 255, 255, 0.2)', borderWidth: 1, opacity: 0.85 }
        : null
      const pointItemStyle = unlockedHighlight
        ? {
            ...(gradientItemStyle || {}),
            borderColor: '#ffffff',
            borderWidth: 3,
            opacity: 1,
            shadowBlur: 22,
            shadowColor: 'rgba(234, 91, 12, 1)',
          }
        : gradientItemStyle

      const dataPoint = {
        value: zAxisKPI !== 'none' && zValue != null ? [xValue, yValue, zValue, symbolSize] : [xValue, yValue],
        // Anonymized points never carry the real name — the tooltip/on-chart
        // label formatters both fall back to '' for a nullish name.
        name: anonymizePoint ? null : (cell.name || cell.id),
        symbolSize,
        // Z-axis gradient colour and/or the unlocked-highlight ring; otherwise
        // the point inherits its colour from the category series.
        ...(pointItemStyle ? { itemStyle: pointItemStyle } : {}),
        emphasis: {
          itemStyle: {
            borderColor: '#ea5b0c',
            borderWidth: 2,
            opacity: 1
          },
          scale: 1.2
        },
        // Anonymized points embed ONLY { id, handle, __anon } — never the full
        // `cell` (which carries name/manufacturer/chemistry) — so identity
        // never reaches the DOM/chart state for a locked point. `category`
        // stays on the point either way: it's the aggregate group label
        // already shown in the legend (distribution stays visible by design),
        // not per-cell identity.
        ...(anonymizePoint ? buildAnonPointData(cell) : { cell }),
        category,
        zValue
      }

      // Group by category for legend
      if (!grouped[category]) grouped[category] = []
      grouped[category].push(dataPoint)
      allData.push(dataPoint)
    })

    // Third pass: create shadow data points for locked cells
    lockedCells.forEach(cell => {
      const xValue = getKPIValue(cell, xAxisKPI)
      const yValue = getKPIValue(cell, yAxisKPI)
      const zValue = zAxisKPI !== 'none' ? getKPIValue(cell, zAxisKPI) : null

      if (xValue == null || yValue == null) return

      shadowData.push({
        value: zAxisKPI !== 'none' && zValue != null ? [xValue, yValue, zValue, 6] : [xValue, yValue],
        name: cell.name || cell.id,
        symbolSize: 6,
        cell,
        zValue
      })
    })

    if (skippedCount > 0) {
      console.warn(`[FreePlot] Skipped ${skippedCount} cells due to missing X or Y values (X: ${xAxisKPI}, Y: ${yAxisKPI})`)
    }
    console.log(`[FreePlot] Generated ${allData.length} data points + ${shadowData.length} shadow points`)

    // Sort categories alphabetically, but put 'Analysis Ongoing' and 'Unknown' at the end
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const aLower = a.toLowerCase()
      const bLower = b.toLowerCase()
      const aIsOngoing = aLower.includes('analysis ongoing')
      const bIsOngoing = bLower.includes('analysis ongoing')
      const aIsUnknown = aLower === 'unknown'
      const bIsUnknown = bLower === 'unknown'
      const aIsSpecial = aIsOngoing || aIsUnknown
      const bIsSpecial = bIsOngoing || bIsUnknown

      // Special items go to the end
      if (aIsSpecial && !bIsSpecial) return 1
      if (!aIsSpecial && bIsSpecial) return -1
      // Unknown after Analysis Ongoing
      if (aIsUnknown && bIsOngoing) return 1
      if (aIsOngoing && bIsUnknown) return -1
      // Regular alphabetic sort
      return a.localeCompare(b)
    })

    return {
      chartDataByCategory: grouped,
      categories: sortedCategories,
      allChartData: allData,
      shadowChartData: shadowData,
      sizeRange: { min: minSize, max: maxSize }
    }
  }, [cells, xAxisKPI, yAxisKPI, zAxisKPI, colorBy, sizeBy, activePreset.value, isDemo])

  // Calculate trendline data for evolution presets (when X-axis is 'year' in Guided mode)
  const isGuidedMode = plotMode.value === 'user'
  const trendlineData = useMemo(() => {
    if (xAxisKPI !== 'year' || !isGuidedMode) {
      currentRegression.value = null
      return null
    }

    const regression = calculateLinearRegression(allChartData)
    console.log('[FreePlot Trend] Attempting trendline, regression:', regression)

    if (!regression) {
      console.log('[FreePlot Trend] No regression data available')
      currentRegression.value = null
      return null
    }

    // Store regression data in signal for insight generation (always, even if unreliable)
    currentRegression.value = regression

    // Skip visual trendline when regression is not reliable enough
    if (!isRegressionReliable(regression)) {
      console.log('[FreePlot Trend] Regression unreliable (n=%d, R²=%s, span=%d years) — hiding trendline',
        regression.n, regression.r2.toFixed(3), regression.maxX - regression.minX)
      return null
    }

    const startY = regression.slope * regression.minX + regression.intercept
    const endY = regression.slope * regression.maxX + regression.intercept

    console.log('[FreePlot Trend] Trendline from', [regression.minX, startY], 'to', [regression.maxX, endY])

    // Calculate annual change as percentage
    const avgY = (startY + endY) / 2
    const annualChange = avgY > 0 ? ((regression.slope / avgY) * 100).toFixed(1) : '0'
    const trendDirection = regression.slope > 0 ? '+' : ''

    return {
      minX: regression.minX,
      maxX: regression.maxX,
      startY,
      endY,
      annualChange,
      trendDirection,
      r2: regression.r2.toFixed(2)
    }
  }, [allChartData, xAxisKPI, isGuidedMode])

  // Calculate bounding boxes for each category (only in Guided mode with active preset)
  const boundingBoxes = useMemo(() => {
    // Only show bounding boxes when a preset is active in Guided mode
    if (!activePreset.value || !isGuidedMode || xAxisKPI === 'year' || categories.length < 2) {
      currentClusterData.value = null
      return null
    }

    const boxes = categories.map(cat => {
      const points = chartDataByCategory[cat] || []
      if (points.length === 0) return null

      let minX = Infinity, maxX = -Infinity
      let minY = Infinity, maxY = -Infinity
      let sumX = 0, sumY = 0, count = 0

      for (const point of points) {
        const x = point.value?.[0]
        const y = point.value?.[1]
        if (x != null && y != null && !isNaN(x) && !isNaN(y)) {
          minX = Math.min(minX, x)
          maxX = Math.max(maxX, x)
          minY = Math.min(minY, y)
          maxY = Math.max(maxY, y)
          sumX += x
          sumY += y
          count++
        }
      }

      if (count === 0) return null

      return {
        name: cat,
        minX,
        maxX,
        minY,
        maxY,
        avgX: sumX / count,
        avgY: sumY / count,
        count
      }
    }).filter(Boolean)

    // Store cluster data in signal for insight generation
    currentClusterData.value = { categories: boxes }

    console.log('[FreePlot] Bounding boxes calculated:', boxes.length, 'categories')

    return boxes
  }, [chartDataByCategory, categories, xAxisKPI, isGuidedMode])

  // Get KPI metadata
  const xKPI = ALL_KPIS.find(k => k.id === xAxisKPI) || {}
  const yKPI = ALL_KPIS.find(k => k.id === yAxisKPI) || {}
  const zKPI = zAxisKPI !== 'none' ? ALL_KPIS.find(k => k.id === zAxisKPI) : null

  // Define benchmark lines based on user input
  const getBenchmarkLines = () => {
    if (!showBenchmarks) return null

    const lines = []

    // X-axis benchmark line (vertical line)
    if (freePlotBenchmarkX.value != null) {
      lines.push({
        name: `X: ${freePlotBenchmarkX.value} ${xKPI.unit || ''}`,
        xAxis: freePlotBenchmarkX.value
      })
    }

    // Y-axis benchmark line (horizontal line)
    if (freePlotBenchmarkY.value != null) {
      lines.push({
        name: `Y: ${freePlotBenchmarkY.value} ${yKPI.unit || ''}`,
        yAxis: freePlotBenchmarkY.value
      })
    }

    return lines.length > 0 ? lines : null
  }

  const benchmarkLines = getBenchmarkLines()

  // Calculate year axis bounds if year is used
  const getYearAxisConfig = (kpi, chartData, valueIndex) => {
    console.log('[getYearAxisConfig] Called with:', { kpiId: kpi?.id, dataLength: chartData?.length, valueIndex })

    if (!kpi || kpi.id !== 'year') {
      console.log('[getYearAxisConfig] Not year axis, returning empty config')
      return {}
    }

    // Ensure data is valid array
    if (!Array.isArray(chartData) || chartData.length === 0) {
      console.warn('[getYearAxisConfig] Invalid or empty chart data')
      return {}
    }

    // Extract values from the specified index (0 for X, 1 for Y, 2 for Z)
    const values = chartData.map(d => d?.value?.[valueIndex]).filter(v => v != null && !isNaN(v) && isFinite(v))
    console.log('[getYearAxisConfig] Extracted values:', values.slice(0, 5), '... (total:', values.length, ')')

    if (values.length === 0) {
      console.warn('[getYearAxisConfig] No valid values found')
      return {}
    }

    // Floor/ceil to get integer bounds, but keep data as decimals
    const minYear = Math.floor(Math.min(...values))
    const maxYear = Math.ceil(Math.max(...values))

    console.log('[getYearAxisConfig] Year bounds:', { minYear, maxYear })

    // Validate bounds
    if (!isFinite(minYear) || !isFinite(maxYear) || minYear >= maxYear) {
      console.warn('[getYearAxisConfig] Invalid bounds')
      return {}
    }

    return {
      min: minYear,
      max: maxYear,
      // Don't set interval - let ECharts decide, we just control the formatter
    }
  }

  // Build chart option (2D or 3D based on Z-axis selection)
  const chartOption = is3DMode ? build3DChartOption() : build2DChartOption()

  // Build 3D Chart Option
  function build3DChartOption() {
    // Log to debug coordinate system registration
    console.log('[FreePlot 3D] Building 3D chart option with', categories.length, 'categories')

    // Grid configuration for watermark (3D uses different positioning)
    const gridConfig3D = {
      left: 80,
      right: 40,
      top: showLegend ? 80 : 40,
      bottom: 80
    }

    return {
      backgroundColor: 'transparent',
      graphic: [getWatermarkGraphic({
        position: 'top-right',
        opacity: 0.12,
        width: 236,
        height: 60,
        grid: gridConfig3D
      })],
      tooltip: {
        show: true,
        trigger: 'item',
        triggerOn: 'mousemove',
        backgroundColor: 'rgba(20, 20, 20, 0.95)',
        borderColor: 'rgba(234, 91, 12, 0.5)',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12
        },
        formatter: (params) => {
          // echarts-gl scatter3D: params.data is the object we passed
          const data = params.data || {}

          // Demo scraping-friction (Task 11): anonymized points never carry
          // `cell` — show a minimal locked hint instead of the full details.
          // The 3D tooltip is mousemove-triggered and NOT `enterable` (unlike
          // the 2D click tooltip below), so an interactive Unlock button here
          // would disappear before it could be clicked — point users at the
          // 2D view, which has the real Unlock affordance.
          if (data.__anon) {
            return `
              <div style="font-family:'Roboto',sans-serif;font-weight:400;font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Locked cell</div>
              <div style="margin-top:4px;font-family:'Roboto',sans-serif;color:#94a3b8;font-size:11px;">Switch to the 2D view to unlock.</div>
            `
          }

          const cell = data.cell
          const values = data.value || []

          if (!cell) return params.name || ''

          const manufacturer = escapeHtml(cell.manufacturer || cell.general?.manufacturer || 'Unknown')
          // Chemistry is a GATED value — never show it for a demo user who
          // hasn't unlocked this cell (gate on unlock state directly, not on
          // `_gated`, so it can't leak for exempt/benchmarking rows). Matches
          // the 2D tooltip guard (BA, 2026-07-20).
          const chemLocked = isDemoCatalogUnlocked() && !isCellUnlockedToday(cell.product_id || cell.id)
          const chemistry = chemLocked
            ? '<span style="color:#64748b;">Locked</span>'
            : escapeHtml(cell.chemical?.main_chemistry || cell.chemical?.cathode || 'N/A')
          const format = escapeHtml(cell.mechanical?.format_group || cell.mechanical?.format || 'N/A')

          return `
            <div style="font-family:'Roboto',sans-serif;font-weight:400;font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">${manufacturer}</div>
            <div style="font-family:'Big John',sans-serif;font-weight:600;margin-bottom:8px;color:#ea5b0c;text-transform:uppercase;font-size:11px;margin-top:2px;">${escapeHtml(data.name || params.name || '')}</div>
            <div style="margin:4px 0;font-family:'Roboto',sans-serif;"><span style="color:#94a3b8;">${xKPI.label}:</span> <span style="color:#fff;font-weight:500;">${formatValue(values[0], xKPI.unit)} ${xKPI.unit}</span></div>
            <div style="margin:4px 0;font-family:'Roboto',sans-serif;"><span style="color:#94a3b8;">${yKPI.label}:</span> <span style="color:#fff;font-weight:500;">${formatValue(values[1], yKPI.unit)} ${yKPI.unit}</span></div>
            ${zKPI && values[2] != null ? `<div style="margin:4px 0;font-family:'Roboto',sans-serif;"><span style="color:#94a3b8;">${zKPI.label}:</span> <span style="color:#fff;font-weight:500;">${formatValue(values[2], zKPI.unit)} ${zKPI.unit}</span></div>` : ''}
            <div style="margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.1);color:#94a3b8;font-size:11px;font-family:'Roboto',sans-serif;">
              ${chemistry} • ${format}
            </div>
          `
        }
      },
      // Legend - Same as 2D plot for consistency
      legend: showLegend ? {
        show: true,
        type: 'scroll',
        orient: 'horizontal',
        left: 'center',
        top: 8,
        backgroundColor: 'rgba(26, 26, 26, 0.7)',
        borderColor: 'rgba(234, 91, 12, 0.25)',
        borderWidth: 1,
        borderRadius: 8,
        padding: [8, 16],
        itemGap: 16,
        itemWidth: 12,
        itemHeight: 12,
        selectedMode: 'multiple',
        data: [
          ...categories.map(cat => ({
            name: cat,
            icon: 'circle',
            itemStyle: {
              color: getCategoryColor(cat, colorBy),
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1
            }
          })),
          ...(shadowChartData.length > 0 ? [{
            name: 'Locked',
            icon: 'circle',
            itemStyle: {
              color: 'rgba(100, 116, 139, 0.35)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1
            }
          }] : [])
        ],
        textStyle: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: 11,
          color: '#cbd5e1',
          fontWeight: 400
        },
        pageTextStyle: {
          color: '#ea5b0c',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 500
        },
        pageIconColor: '#ea5b0c',
        pageIconInactiveColor: '#475569',
        pageIconSize: 10,
        icon: 'circle',
        emphasis: {
          selectorLabel: {
            show: true,
            distance: 5,
            color: '#ea5b0c'
          }
        }
      } : { show: false },
      grid3D: {
        boxWidth: 100,
        boxHeight: 100,
        boxDepth: 100,
        viewControl: {
          projection: 'perspective',
          autoRotate: false,
          distance: 200,
          alpha: 25,
          beta: 45,
          minDistance: 100,
          maxDistance: 400
        },
        light: {
          main: {
            intensity: 1.2,
            shadow: true
          },
          ambient: {
            intensity: 0.4
          }
        },
        environment: 'none',
        postEffect: {
          enable: false
        }
      },
      xAxis3D: {
        type: 'value',
        name: xKPI.unit ? `${xKPI.label} / ${xKPI.unit}` : xKPI.label,
        ...getYearAxisConfig(xKPI, allChartData, 0),
        nameTextStyle: {
          color: '#e2e8f0',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        },
        axisLabel: {
          color: '#94a3b8',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 10
        },
        splitLine: {
          show: showGrid,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      },
      yAxis3D: {
        type: 'value',
        name: yKPI.unit ? `${yKPI.label} / ${yKPI.unit}` : yKPI.label,
        ...getYearAxisConfig(yKPI, allChartData, 1),
        nameTextStyle: {
          color: '#e2e8f0',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        },
        axisLabel: {
          color: '#94a3b8',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 10
        },
        splitLine: {
          show: showGrid,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      },
      zAxis3D: {
        type: 'value',
        name: zKPI.unit ? `${zKPI.label} / ${zKPI.unit}` : zKPI.label,
        ...getYearAxisConfig(zKPI, allChartData, 2),
        nameTextStyle: {
          color: '#e2e8f0',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        },
        axisLabel: {
          color: '#94a3b8',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 10
        },
        splitLine: {
          show: showGrid,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      },
      series: [
        // Shadow series for locked cells in 3D
        ...(shadowChartData.length > 0 ? [{
          name: 'Locked',
          type: 'scatter3D',
          data: shadowChartData.filter(d => d.value.length >= 3).map(d => ({
            value: [d.value[0], d.value[1], d.value[2]],
            name: d.name,
            cell: d.cell
          })),
          symbolSize: 5,
          itemStyle: {
            color: 'rgba(100, 116, 139, 0.35)',
            opacity: 0.4
          },
          emphasis: {
            itemStyle: {
              color: 'rgba(100, 116, 139, 0.5)',
              borderWidth: 1,
              borderColor: 'rgba(148, 163, 184, 0.4)'
            }
          }
        }] : []),
        // Accessible cell series per category
        ...categories.map(cat => {
          const seriesData = chartDataByCategory[cat].map(d => ({
            value: [d.value[0], d.value[1], d.value[2]],
            name: d.name,
            // Anonymized points (demo, gated dim + not unlocked today) carry
            // only { id, handle, __anon } — never the full cell — matching
            // the 2D path.
            ...(d.__anon ? { __anon: true, id: d.id, handle: d.handle } : { cell: d.cell })
          }))

          console.log(`[FreePlot 3D] Series "${cat}": ${seriesData.length} points`)

          return {
            name: cat,
            type: 'scatter3D',
            data: seriesData,
            symbolSize: sizeBy !== 'none' ? (dataItem, params) => {
              // Access symbolSize from original dataPoint via params.dataIndex
              const originalData = chartDataByCategory[cat][params.dataIndex]
              return originalData?.symbolSize || 8
            } : 8,
            label: {
              show: freePlotShowLabels.value,
              formatter: (params) => params.data.name || '',
              textStyle: {
                fontSize: 10,
                fontFamily: 'Roboto, sans-serif',
                color: '#94a3b8',
                backgroundColor: 'rgba(13, 13, 13, 0.7)',
                padding: [2, 4],
                borderRadius: 3
              }
            },
            itemStyle: {
              color: getCategoryColor(cat, colorBy),
              opacity: 0.8
            },
            emphasis: {
              itemStyle: {
                borderColor: '#ea5b0c',
                borderWidth: 2,
                opacity: 1
              }
            }
          }
        })
      ]
    }
  }

  // Build 2D Chart Option
  function build2DChartOption() {
    // Grid configuration for watermark positioning
    const gridConfig = {
      left: 80,
      right: 40,
      top: showLegend ? 80 : 40,
      bottom: 80,
      containLabel: true
    }

    return {
    backgroundColor: 'transparent',
    graphic: [getWatermarkGraphic({
      position: 'top-right',
      opacity: 0.12,
      width: 236,
      height: 60,
      grid: gridConfig
    })],
    tooltip: {
      trigger: 'item',
      enterable: true, // Allow mouse to enter tooltip to click links
      confine: true, // Keep tooltip within chart bounds
      triggerOn: 'click', // Show tooltip on click only
      hideDelay: 0, // Don't auto-hide
      // The formatter renders its own gradient "card" (matching the Explorer
      // factsheet grid cards), so strip ECharts' default box entirely.
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      borderWidth: 0,
      padding: 0,
      extraCssText: 'box-shadow:none;background:transparent;border:none;',
      textStyle: {
        color: '#fff',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12
      },
      formatter: (params) => {
        const data = params.data

        // Shared styling so every tooltip branch reads like a factsheet grid
        // card (gradient surface, Big John title, Roboto specs). See
        // css/components/factsheet-grid-cards.css — kept in sync by eye.
        // white-space:normal is CRITICAL: ECharts sets white-space:nowrap on the
        // tooltip container and it INHERITS, so without this reset the title and
        // spec labels never wrap — they overrun the card / overlap the value.
        // Only the value (SPEC_VAL) opts back into nowrap.
        // width:max-content → the card grows to fit its content (so a spec label
        // like "Power / peak / gravimetric" and the button texts each stay on one
        // line), capped by max-width; below that it falls back to wrapping.
        const CARD = `position:relative;width:max-content;min-width:230px;max-width:340px;box-sizing:border-box;overflow:hidden;white-space:normal;background:linear-gradient(135deg,rgba(50,50,50,0.94) 0%,rgba(30,30,30,0.97) 100%);border:1px solid rgba(255,255,255,0.08);border-radius:12px;padding:16px;box-shadow:0 8px 32px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.05);font-family:'Roboto',sans-serif;`
        const TITLE = `font-family:'Big John','Roboto',sans-serif;font-size:15px;font-weight:700;color:#fff;text-transform:uppercase;line-height:1.2;margin:0 24px 4px 0;overflow-wrap:break-word;`
        const SPECS = `display:flex;flex-direction:column;gap:4px;margin-bottom:12px;`
        // Grid (1fr auto): the value column takes exactly what it needs (nowrap)
        // and the label column gets the rest and WRAPS — columns can't overlap
        // the way a flex space-between row did with a long label + nowrap value.
        const SPEC_ROW = `display:grid;grid-template-columns:1fr auto;gap:8px;align-items:baseline;padding:6px 10px;background:rgba(255,255,255,0.02);border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.2);`
        const SPEC_LABEL = `min-width:0;color:rgba(255,255,255,0.6);font-family:'Roboto',sans-serif;font-size:12px;line-height:1.3;overflow-wrap:break-word;`
        const SPEC_VAL = `justify-self:end;display:flex;align-items:baseline;gap:3px;font-size:12px;font-weight:600;white-space:nowrap;`
        const SPEC_NUM = `font-family:'Big John',sans-serif;color:rgba(255,255,255,0.9);`
        const SPEC_UNIT = `font-family:'Roboto',sans-serif;font-weight:400;color:rgba(255,255,255,0.7);`
        const FOOTER = `display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;`
        const BADGE = `display:inline-block;padding:3px 9px;font-family:'Roboto',sans-serif;font-size:10.5px;font-weight:500;border-radius:4px;text-transform:uppercase;`
        const BADGE_ORANGE = `background:rgba(234,91,12,0.15);color:#ea5b0c;border:1px solid rgba(234,91,12,0.3);`
        const BADGE_LOCKED = `background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.5);border:1px solid rgba(255,255,255,0.12);`
        const ACTIONS = `display:flex;gap:6px;`
        const BTN = `flex:1;padding:6px 12px;border:1px solid;border-radius:4px;font-family:'Roboto',sans-serif;font-size:11px;font-weight:500;white-space:nowrap;text-align:center;cursor:pointer;transition:all 0.2s;`
        const BTN_ORANGE = `background:rgba(234,91,12,0.15);border-color:rgba(234,91,12,0.4);color:#ea5b0c;`
        const CLOSE_BTN = `position:absolute;top:8px;right:8px;width:22px;height:22px;padding:0;background:rgba(234,91,12,0.2);border:1px solid rgba(234,91,12,0.4);border-radius:50%;color:#ea5b0c;font-size:12px;line-height:20px;cursor:pointer;font-weight:bold;font-family:Arial,sans-serif;`
        const closeBtnHtml = `<button onclick="window.__closeTooltip()" style="${CLOSE_BTN}" onmouseover="this.style.background='rgba(234,91,12,0.4)';" onmouseout="this.style.background='rgba(234,91,12,0.2)';">×</button>`
        const specRow = (label, num, unit) =>
          `<div style="${SPEC_ROW}"><span style="${SPEC_LABEL}">${escapeHtml(label)}</span><span style="${SPEC_VAL}"><span style="${SPEC_NUM}">${escapeHtml(String(num))}</span><span style="${SPEC_UNIT}">${escapeHtml(unit)}</span></span></div>`
        // Header thumbnail (top-left, like the factsheet card). Uses the cell's
        // signed thumbnail_url (already the BLURRED variant for a locked cell,
        // so this never leaks a locked cell's sharp photo). Hidden if it fails
        // to load. NOT shown in the anon branch, which stays identity-free.
        const THUMB = `width:48px;height:48px;flex:0 0 auto;border-radius:8px;object-fit:cover;border:1px solid rgba(255,255,255,0.08);background:linear-gradient(135deg,rgba(40,40,40,0.6) 0%,rgba(20,20,20,0.85) 100%);`
        const HEADER = `display:flex;gap:10px;align-items:flex-start;margin-bottom:12px;`
        const HEAD_COL = `min-width:0;flex:1 1 auto;`
        const HEAD_TITLE = `font-family:'Big John','Roboto',sans-serif;font-size:15px;font-weight:700;text-transform:uppercase;line-height:1.2;margin:0;overflow-wrap:break-word;`
        const HEAD_SUB = `font-family:'Roboto',sans-serif;font-size:12px;font-weight:400;color:rgba(255,255,255,0.6);margin:0 0 2px;padding-right:18px;`
        const thumbHtml = (url) => url ? `<img src="${escapeHtml(String(url))}" alt="" style="${THUMB}" onerror="this.style.display='none';"/>` : ''
        // Manufacturer on top, cell name below (BA preference).
        const cardHeader = (nameStr, manufacturerHtml, titleColor) =>
          `<div style="${HEADER}">${thumbHtml(cell && cell.thumbnail_url)}<div style="${HEAD_COL}"><div style="${HEAD_SUB}">${manufacturerHtml}</div><div style="${HEAD_TITLE}color:${titleColor};">${escapeHtml(nameStr)}</div></div></div>`

        // Demo scraping-friction (Task 11): a gated dimension is active and
        // this cell hasn't been unlocked today — the point carries ONLY
        // { id, handle, __anon }, never the full cell (no name/chemistry/
        // exact value leak). Offer the same Deep-Dive Unlock as Grid/Table,
        // NOT the PRO-upsell path (this is DEMO exploration friction, not a
        // paywall) — enterable:true + triggerOn:'click' above make the button
        // reliably clickable, same as the compare/view-details buttons below.
        if (data.__anon) {
          // Fix #5 (Task 11 review, XSS hardening): `data.id`/`data.handle`
          // are backend-generated hex identifiers (see buildAnonPointData),
          // never user input — but this onclick is built by string
          // interpolation into raw HTML, so defend in depth: whitelist both
          // against a strict charset before they're allowed into the
          // attribute. Anything failing the whitelist (should never happen
          // in practice) drops the Unlock button entirely rather than
          // interpolating an unvalidated value into an inline event handler.
          const SAFE_TOKEN_RE = /^[A-Za-z0-9_-]+$/
          const safeId = typeof data.id === 'string' && SAFE_TOKEN_RE.test(data.id) ? data.id : null
          const safeHandle = typeof data.handle === 'string' && SAFE_TOKEN_RE.test(data.handle) ? data.handle : null
          const unlockButtonHtml = safeId
            ? `<div style="${ACTIONS}">
                <button onclick="window.__unlockPlotCell('${safeId}', ${safeHandle ? `'${safeHandle}'` : 'null'})"
                        style="${BTN}${BTN_ORANGE}"
                        onmouseover="this.style.background='rgba(234,91,12,0.25)';this.style.borderColor='rgba(234,91,12,0.6)';"
                        onmouseout="this.style.background='rgba(234,91,12,0.15)';this.style.borderColor='rgba(234,91,12,0.4)';">
                  Unlock (1 deep dive)
                </button>
              </div>`
            : ''
          return `
            <div style="${CARD}">
              ${closeBtnHtml}
              <div style="${TITLE}font-size:11px;letter-spacing:0.06em;color:rgba(255,255,255,0.55);">Locked cell</div>
              <div style="font-family:'Roboto',sans-serif;font-size:12px;line-height:1.45;color:rgba(255,255,255,0.75);margin:0 0 12px;overflow-wrap:break-word;word-break:break-word;">
                Unlock to see this cell's exact values and identity.
              </div>
              ${unlockButtonHtml}
            </div>
          `
        }

        const cell = data.cell
        if (!cell) return params.name || ''
        const cellId = cell.product_id || cell.id

        // Locked (shadow) cells: minimal tooltip with upgrade hint. DEMO users
        // have the full catalogue unlocked (unlock = deep-dive, not PRO), so
        // skip this PRO-paywall branch for them and fall through to the normal
        // tooltip, which gates on isCellLockedAtLimit (deep-dive/limit-aware).
        if (cell.is_locked && !isDemo) {
          const manufacturer = escapeHtml(cell.manufacturer || cell.general?.manufacturer || 'Unknown')
          return `
            <div style="${CARD}">
              ${closeBtnHtml}
              ${cardHeader(params.name, manufacturer, 'rgba(255,255,255,0.75)')}
              <div style="${SPECS}">
                ${specRow(xKPI.label, formatValue(data.value[0], xKPI.unit), xKPI.unit)}
                ${specRow(yKPI.label, formatValue(data.value[1], yKPI.unit), yKPI.unit)}
                ${zKPI && data.zValue != null ? specRow(zKPI.label, formatValue(data.zValue, zKPI.unit), zKPI.unit) : ''}
              </div>
              <div style="display:flex;align-items:center;gap:6px;color:rgba(255,255,255,0.5);font-size:11px;font-family:'Roboto',sans-serif;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                Upgrade to unlock this cell
              </div>
            </div>
          `
        }

        const slug = generateCellSlug(cell)
        const cellUrl = `/explorer?cell=${slug}&section=factsheet`
        const lockedAtLimit = isCellLockedAtLimit(cellId)
        const inCompare = isInCompare(cellId)
        const compareText = inCompare ? '✓ In Compare' : '+ Add to Compare'
        const compareStyle = inCompare
          ? 'background:rgba(13,124,153,0.2);border-color:rgba(13,124,153,0.5);color:#0d7c99;'
          : 'background:rgba(234,91,12,0.15);border-color:rgba(234,91,12,0.4);color:#ea5b0c;'

        const manufacturer = escapeHtml(cell.manufacturer || cell.general?.manufacturer || 'Unknown')

        // Fix #2 (Task 11 review, governing-rule parity): Grid/Table hide
        // chemistry for a demo cell unconditionally (isChemistryLockedForDemo —
        // true whenever demo + not-unlocked-today + has a chemistry value),
        // regardless of which dimension is plotted. This NAMED branch is
        // reached both for free-only dims (never anonymized) and for a
        // gated-dim cell already unlocked today (isChemistryLockedForDemo is
        // false then) — so masking here only ever hides chemistry in the
        // free-only-dim, not-yet-unlocked case Grid/Table would also hide it.
        // Format/manufacturer/name stay untouched (only chemistry is gated by
        // this rule).
        // Gated-value guard for the free view (BA, 2026-07-20): chemistry is a
        // GATED value and must never show for a demo user who hasn't unlocked
        // this cell — even on free axes (this named branch), and even for an
        // internal/exempt account whose rows carry no `_gated` set. Gate on the
        // unlock state directly rather than isChemistryLockedForDemo (which also
        // requires a non-empty `_gated`, so chemistry leaked for exempt rows /
        // benchmarking data that doesn't carry gated_fields).
        const chemLocked = isDemoCatalogUnlocked() && !isCellUnlockedToday(cellId)
        const chemistry = escapeHtml(cell.chemical?.main_chemistry || cell.chemical?.cathode || 'N/A')
        const format = escapeHtml(cell.mechanical?.format_group || cell.mechanical?.format || 'N/A')
        const chemistryBadge = chemLocked
          ? `<span style="${BADGE}${BADGE_LOCKED}">Locked</span>`
          : `<span style="${BADGE}${BADGE_ORANGE}">${chemistry}</span>`
        const formatBadge = `<span style="${BADGE}${BADGE_ORANGE}">${format}</span>`
        const viewDetailsHtml = lockedAtLimit
          ? `<span class="cell-tooltip-locked-link" onclick="window.__openDemoLimitModal && window.__openDemoLimitModal()" style="${BTN}${BTN_ORANGE}display:flex;align-items:center;justify-content:center;text-decoration:none;" onmouseover="this.style.background='rgba(234,91,12,0.25)';this.style.borderColor='rgba(234,91,12,0.6)';" onmouseout="this.style.background='rgba(234,91,12,0.15)';this.style.borderColor='rgba(234,91,12,0.4)';">View Details →</span>`
          : `<a href="${cellUrl}" style="${BTN}${BTN_ORANGE}display:flex;align-items:center;justify-content:center;text-decoration:none;" onmouseover="this.style.background='rgba(234,91,12,0.25)';this.style.borderColor='rgba(234,91,12,0.6)';" onmouseout="this.style.background='rgba(234,91,12,0.15)';this.style.borderColor='rgba(234,91,12,0.4)';">View Details →</a>`

        return `
          <div style="${CARD}">
            ${closeBtnHtml}
            ${cardHeader(params.name, manufacturer, '#fff')}
            <div style="${SPECS}">
              ${specRow(xKPI.label, formatValue(data.value[0], xKPI.unit), xKPI.unit)}
              ${specRow(yKPI.label, formatValue(data.value[1], yKPI.unit), yKPI.unit)}
              ${zKPI && data.zValue != null ? specRow(zKPI.label, formatValue(data.zValue, zKPI.unit), zKPI.unit) : ''}
            </div>
            <div style="${FOOTER}">${chemistryBadge}${formatBadge}</div>
            <div style="${ACTIONS}">
              <button onclick="window.__toggleCompare('${cellId}')" style="${BTN}${compareStyle}" onmouseover="this.style.opacity='0.8';" onmouseout="this.style.opacity='1';">${compareText}</button>
              ${viewDetailsHtml}
            </div>
          </div>
        `
      }
    },
    // Legend - Unified design with Ragone plot
    legend: showLegend ? {
      show: true,
      type: 'scroll',
      orient: 'horizontal',
      left: 'center',
      top: 8,
      backgroundColor: 'rgba(26, 26, 26, 0.7)',
      borderColor: 'rgba(234, 91, 12, 0.25)',
      borderWidth: 1,
      borderRadius: 8,
      padding: [8, 16],
      itemGap: 16,
      itemWidth: 12,
      itemHeight: 12,
      selectedMode: 'multiple', // Enable click to toggle visibility
      data: [
        ...categories.map(cat => ({
          name: cat,
          icon: 'circle',
          itemStyle: {
            color: getCategoryColor(cat, colorBy),
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderWidth: 1
          }
        })),
        ...(shadowChartData.length > 0 ? [{
          name: 'Locked',
          icon: 'circle',
          itemStyle: {
            color: 'rgba(100, 116, 139, 0.35)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1
          }
        }] : [])
      ],
      textStyle: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 11,
        color: '#cbd5e1',
        fontWeight: 400
      },
      pageTextStyle: {
        color: '#ea5b0c',
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 500
      },
      pageIconColor: '#ea5b0c',
      pageIconInactiveColor: '#475569',
      pageIconSize: 10,
      icon: 'circle',
      emphasis: {
        selectorLabel: {
          show: true,
          distance: 5,
          color: '#ea5b0c'
        }
      }
    } : { show: false },
    grid: gridConfig,
    xAxis: {
      type: xAxisScale === 'log' ? 'log' : 'value',
      name: xKPI.unit ? `${xKPI.label} / ${xKPI.unit}` : xKPI.label,
      nameLocation: 'middle',
      nameGap: 45,
      ...getYearAxisConfig(xKPI, allChartData, 0),
      nameTextStyle: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        fontWeight: 500,
        color: '#e2e8f0'
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.15)',
          width: 1.5
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.1)',
          width: 1
        },
        length: 4
      },
      axisLabel: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 11,
        color: '#94a3b8',
        fontWeight: 400,
        formatter: (value) => {
          // For year, show as integer without decimals
          if (xKPI.id === 'year') return Math.round(value).toString()
          // Always show full numbers, no k-notation
          if (value >= 1000) return value.toFixed(0)
          if (value >= 100) return value.toFixed(0)
          return value.toFixed(1)
        }
      },
      splitLine: {
        show: showGrid,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.05)',
          width: 1,
          type: 'solid'
        }
      }
    },
    yAxis: {
      type: yAxisScale === 'log' ? 'log' : 'value',
      name: yKPI.unit ? `${yKPI.label} / ${yKPI.unit}` : yKPI.label,
      nameLocation: 'middle',
      nameGap: 60,
      ...getYearAxisConfig(yKPI, allChartData, 1),
      nameTextStyle: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        fontWeight: 500,
        color: '#e2e8f0'
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.15)',
          width: 1.5
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.1)',
          width: 1
        },
        length: 4
      },
      axisLabel: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 11,
        color: '#94a3b8',
        fontWeight: 400,
        formatter: (value) => {
          // For year, show as integer without decimals
          if (yKPI.id === 'year') return Math.round(value).toString()
          // Always show full numbers, no k-notation
          if (value >= 1000) return value.toFixed(0)
          if (value >= 100) return value.toFixed(0)
          return value.toFixed(1)
        }
      },
      splitLine: {
        show: showGrid,
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.05)',
          width: 1,
          type: 'solid'
        }
      }
    },
    // Build series for each category (for legend filtering)
    series: [
      // Shadow series for locked cells — grey, minimal interactivity
      ...(shadowChartData.length > 0 ? [{
        name: 'Locked',
        type: 'scatter',
        data: shadowChartData,
        symbol: 'circle',
        symbolSize: 6,
        z: 1,
        itemStyle: {
          color: 'rgba(100, 116, 139, 0.35)',
          borderWidth: 0
        },
        emphasis: {
          scale: 1.1,
          itemStyle: {
            color: 'rgba(100, 116, 139, 0.5)',
            borderWidth: 1,
            borderColor: 'rgba(148, 163, 184, 0.4)'
          }
        },
        label: { show: false }
      }] : []),
      ...categories.map((cat, index) => {
        const seriesData = chartDataByCategory[cat]
        console.log(`[FreePlot 2D] Series "${cat}": ${seriesData?.length || 0} points`)
        return {
          name: cat,
          type: connectPoints ? 'line' : 'scatter',
          data: seriesData,
          symbol: markerShape,
          symbolSize: (data) => data[2] || 10, // Use stored symbolSize
        showSymbol: true,
        itemStyle: {
          color: getCategoryColor(cat, colorBy),
          opacity: 0.8,
          borderWidth: 1,
          borderColor: 'rgba(255, 255, 255, 0.2)'
        },
        lineStyle: connectPoints ? {
          width: 1,
          opacity: 0.3,
          color: getCategoryColor(cat, colorBy)
        } : undefined,
        label: {
          show: freePlotShowLabels.value,
          position: 'top',
          formatter: (params) => params.data.name || '',
          fontSize: 10,
          fontFamily: 'Roboto, sans-serif',
          color: '#94a3b8',
          backgroundColor: 'rgba(13, 13, 13, 0.7)',
          padding: [2, 4],
          borderRadius: 3
        },
        emphasis: {
          focus: 'series',
          blurScope: 'coordinateSystem',
          itemStyle: {
            borderColor: '#ea5b0c',
            borderWidth: 2,
            opacity: 1
          },
          scale: 1.3
        },
        blur: {
          itemStyle: {
            opacity: 0.15
          }
        },
        // Add benchmark lines to first series only
        ...(index === 0 && benchmarkLines ? {
          markLine: {
            silent: true,
            symbol: 'none',
            label: {
              show: true,
              position: 'end',
              formatter: '{b}',
              fontFamily: 'Roboto, sans-serif',
              fontSize: 11,
              color: '#94a3b8',
              backgroundColor: 'rgba(13, 13, 13, 0.8)',
              padding: [4, 8],
              borderRadius: 4
            },
            lineStyle: {
              type: 'dashed',
              color: '#475569',
              width: 2,
              opacity: 0.6
            },
            data: benchmarkLines,
            animation: false
          }
        } : {}),
        // Add bounding box (markArea) for this category
        ...(boundingBoxes ? (() => {
          const box = boundingBoxes.find(b => b.name === cat)
          if (!box) return {}
          const catColor = getCategoryColor(cat, colorBy)
          return {
            markArea: {
              silent: true,
              itemStyle: {
                color: hexToRgba(catColor, 0.06),
                borderColor: hexToRgba(catColor, 0.2),
                borderWidth: 1,
                borderType: 'dashed'
              },
              data: [[
                { coord: [box.minX, box.minY] },
                { coord: [box.maxX, box.maxY] }
              ]],
              animation: false
            }
          }
        })() : {})
        }
      }),
      // Trendline for Evolution presets (when X-axis is 'year')
      ...(xAxisKPI === 'year' && trendlineData ? [{
        name: 'Trend',
        type: 'line',
        data: [
          [trendlineData.minX, trendlineData.startY],
          [trendlineData.maxX, trendlineData.endY]
        ],
        symbol: 'none',
        lineStyle: {
          type: 'dashed',
          color: '#ea5b0c',
          width: 3,
          opacity: 1
        },
        label: {
          show: true,
          position: 'end',
          formatter: `Trend: ${trendlineData.trendDirection}${trendlineData.annualChange}%/Jahr (R²=${trendlineData.r2})`,
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
          fontWeight: 600,
          color: '#ea5b0c',
          backgroundColor: 'rgba(13, 13, 13, 0.95)',
          padding: [6, 10],
          borderRadius: 4,
          borderColor: '#ea5b0c',
          borderWidth: 1
        },
        z: 100,
        silent: true // Don't trigger interactions
      }] : [])
    ],
      // Toolbox
      toolbox: {
        feature: {
          dataZoom: {
            show: true,
            yAxisIndex: 'none',
            title: {
              zoom: 'Zoom',
              back: 'Reset Zoom'
            }
          },
          restore: {
            show: false
          },
          // For demo users, replace the native saveAsImage (which downloads
          // directly) with a visually-identical custom button that opens the
          // export upsell + records the signal instead of exporting. Non-demo
          // users keep the real saveAsImage. The icon path is the ECharts
          // built-in saveAsImage icon, so the button looks unchanged.
          ...(isDemo
            ? {
                myExport: {
                  show: true,
                  title: 'Save as Image',
                  icon: 'path://M4.7,22.9L29.3,45.5L54.7,23.4M4.6,43.6L4.6,58L53.8,58L53.8,43.6M29.2,45.1L29.2,0',
                  onclick: () => blockDemoExport('free_plot')
                }
              }
            : {
                saveAsImage: {
                  show: true,
                  name: 'batemo-free-plot',
                  title: 'Save as Image'
                }
              })
        },
        right: 20,
        top: 20,
        itemSize: 16,
        itemGap: 12,
        iconStyle: {
          borderColor: '#cbd5e1'
        },
        emphasis: {
          iconStyle: {
            borderColor: '#ea5b0c'
          }
        }
      },
      // Data Zoom
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none'
        },
        {
          type: 'inside',
          yAxisIndex: 0,
          filterMode: 'none'
        }
      ]
    }
  }

  if (allChartData.length === 0) {
    return (
      <div style="display:flex;align-items:center;justify-content:center;height:100%;color:#94a3b8;font-family:Roboto,sans-serif;">
        No data available for selected axes
      </div>
    )
  }

  // Key forces remount when critical settings change to prevent rendering issues
  // Include axes in key to ensure clean remount when switching between year and other axes
  // replaceMerge=['series'] replaces all series without remounting (smooth animations)

  // Handler to close tooltip via X button or click outside
  const handleChartReady = (chart) => {
    if (typeof window !== 'undefined') {
      window.__freePlotChartInstance = chart
    }

    // Listen for global close event from X button
    const handleTooltipClose = () => {
      chart.dispatchAction({
        type: 'hideTip'
      })
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('tooltip-close', handleTooltipClose)
      window.addEventListener('tooltip-close', handleTooltipClose)
    }

    // Close tooltip when clicking outside (on chart background)
    chart.on('click', (params) => {
      if (!params.componentType || params.componentType !== 'series') {
        // Click on empty area - close tooltip
        chart.dispatchAction({
          type: 'hideTip'
        })
      }
    })

    // Re-open the tooltip on a cell the user just unlocked from the plot, once
    // the post-unlock explorer reload (demoUnlockVersion) has re-rendered the
    // point with its real (named) data. 'finished' fires on every render, but
    // this only acts while __justUnlockedCellId is set AND only matches once the
    // point carries the unlocked `cell` (anonymized points have none) — so it
    // naturally fires after the reload, never on the stale "Locked" point.
    const reopenJustUnlocked = () => {
      const targetId = typeof window !== 'undefined' ? window.__justUnlockedCellId : null
      if (!targetId) return
      const opt = chart.getOption()
      const series = (opt && opt.series) || []
      for (let si = 0; si < series.length; si++) {
        const data = series[si] && series[si].data
        if (!Array.isArray(data)) continue
        for (let di = 0; di < data.length; di++) {
          const c = data[di] && data[di].cell
          const cid = c && (c.product_id || c.id)
          if (cid != null && String(cid) === String(targetId)) {
            window.__justUnlockedCellId = null
            chart.dispatchAction({ type: 'showTip', seriesIndex: si, dataIndex: di })
            return
          }
        }
      }
    }
    chart.off('finished', reopenJustUnlocked)
    chart.on('finished', reopenJustUnlocked)
  }

  // Legend key for the unlocked-today highlight — a DOM pill overlaid top-left
  // (reliable position + flexbox-aligned dot/text, unlike an ECharts graphic).
  const unlockedCount = isDemo ? unlockedTodayIds.value.size : 0

  return (
    <div style="position:relative;width:100%;height:100%;">
      <EChartsComponent
        key={`free-${colorBy}-${xAxisKPI}-${yAxisKPI}-${is3DMode ? '3d' : '2d'}`}
        option={chartOption}
        style={{ width: '100%', height: '100%' }}
        notMerge={true}
        replaceMerge={['series']}
        onChartReady={handleChartReady}
      />
      {unlockedCount > 0 && (
        <div style={`position:absolute;top:${showLegend ? '46px' : '12px'};left:50%;transform:translateX(-50%);z-index:5;display:flex;align-items:center;gap:7px;padding:4px 10px;background:rgba(20,20,20,0.72);border:1px solid rgba(255,255,255,0.14);border-radius:6px;font-family:'Roboto',sans-serif;font-size:11px;color:rgba(255,255,255,0.82);pointer-events:none;white-space:nowrap;`}>
          <span style="width:11px;height:11px;border-radius:50%;background:#ea5b0c;border:2px solid #fff;box-shadow:0 0 8px rgba(234,91,12,0.9);flex:0 0 auto;"></span>
          Unlocked today ({unlockedCount})
        </div>
      )}
    </div>
  )
}
